import React from "react";
import Marquee from "react-fast-marquee";
import { useLang, UI } from "../i18n";
import { LIGUE, THEMES, TRAVEL, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, CourtLines, MonoTag, GhostText, PlaneIcon } from "../components/motifs";
import { FicheInfo } from "../components/Fiche";
import { getProject } from "../content";
import { ContactSection } from "../components/Layout";

const CODES = ["PAR", "BCN", "MIL", "LON"];

export default function Ligue() {
  useLenis();
  const { t } = useLang();
  const th = THEMES.ligue;
  return (
    <MiniSite theme={th}>
      <PageHero
        code="AST-07 · LIGUE"
        overline={t(UI.nav.ligue)}
        title={t(LIGUE.title)}
        subtitle={t(LIGUE.intro)}
        image={IMG.heroB}
      />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-6 flex flex-wrap items-center gap-3" data-testid="ligue-divisions">
        <MonoTag className="text-white/60">DIVISIONS</MonoTag>
        {LIGUE.divisions.map((d) => (
          <span key={d} className="font-heading text-xs uppercase tracking-wide border rounded-full px-4 py-2"
            style={{ borderColor: `${th.accent}66`, color: th.accent }}>{d}</span>
        ))}
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 ml-auto">
          Director: {LIGUE.director} · {LIGUE.partnersLine}
        </span>
      </div>
      <div data-testid="ligue-next-event" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-10">
        <div className="flex flex-wrap items-center gap-4 border rounded-md px-6 py-4"
          style={{ borderColor: `${th.accent}66`, backgroundColor: "var(--ms-surface)" }}>
          <MonoTag className="blink" style={{ color: th.accent }}>● {t(LIGUE.nextEvent.label)}</MonoTag>
          <span className="font-display text-2xl md:text-3xl uppercase">{LIGUE.nextEvent.name}</span>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/70">{t(LIGUE.nextEvent.date)}</span>
          <a href="#contact" data-testid="ligue-next-cta"
            className="md:ml-auto inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-heading font-semibold uppercase text-xs text-black hover:opacity-85 transition-opacity duration-300"
            style={{ backgroundColor: th.accent }}>
            {t({ fr: "S'inscrire", en: "Register", es: "Inscribirse" })}
          </a>
        </div>
      </div>
      <div className="border-y border-white/10 py-5 bg-black/25">
        <Marquee autoFill speed={50} gradient={false}>
          <span className="font-display text-3xl md:text-5xl uppercase mx-8 text-white">
            THE EUROPEAN CIRCUIT <span style={{ color: th.accent }}>★</span>
          </span>
        </Marquee>
      </div>

      <section className="relative pt-16 overflow-hidden">
        <GhostText text="TEC" className="text-[30vw] top-0 left-0" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 flex justify-center">
          <img src={LIGUE.logo} alt="The European Circuit" data-testid="ligue-logo"
            className="w-56 md:w-72 h-auto object-contain rounded-xl shadow-2xl" style={{ boxShadow: `0 0 80px ${th.accent}44` }} />
        </div>
      </section>

      <section className="py-20 max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {LIGUE.stats.map((s, i) => (
            <Reveal key={s.l.fr} delay={i * 0.08} className="border-t border-white/20 pt-5">
              <div className="font-display text-5xl md:text-6xl" style={{ color: th.accent }}>{s.v}</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mt-2">{t(s.l)}</div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12">
          <Reveal><FicheInfo fiche={getProject("european-circuit")} accent={th.accent} /></Reveal>
        </div>
      </section>

      <section className="relative pb-20 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <CourtLines className="-left-56 bottom-0 w-[560px] h-[560px]" />
        <div className="relative">
          <Reveal>
            <MonoTag style={{ color: th.accent }}>{t(TRAVEL.flight)} PLAN · SAISON</MonoTag>
            <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 mb-8">{t(LIGUE.calTitle)}</h2>
          </Reveal>
          <div className="space-y-3">
            {LIGUE.calendar.map((c, i) => (
              <Reveal key={`${c.stage.fr}-${c.date.fr}`} delay={i * 0.06}>
                <div data-testid={`ligue-stage-${i}`}
                  className="flex items-center gap-5 border border-white/15 bg-white/[0.04] rounded-md px-5 md:px-6 py-5 hover:bg-white/[0.08] transition-colors duration-300">
                  <span className="font-display text-2xl md:text-3xl w-14 shrink-0" style={{ color: th.accent }}>{CODES[i]}</span>
                  <span className="relative w-10 shrink-0 border-t-2 border-dashed border-white/25 hidden sm:block">
                    <PlaneIcon size={13} className="absolute -top-2 left-1/2 -translate-x-1/2 rotate-90 text-white/50" />
                  </span>
                  <span className="font-heading text-base md:text-lg flex-1">{t(c.stage)}</span>
                  <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-white/60">{t(c.date)}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="relative">
          <Reveal>
            <MonoTag style={{ color: th.accent }}>ROSTER · {LIGUE.teams.length} CLUBS</MonoTag>
            <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 mb-8">{t(LIGUE.teamsTitle)}</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {LIGUE.teams.map((team, i) => (
              <Reveal key={team} delay={i * 0.05}>
                <span className="inline-flex items-center gap-3 font-heading uppercase text-sm border border-white/20 rounded-full px-5 py-3 hover:bg-black/30 transition-colors duration-300">
                  <span className="font-display" style={{ color: th.accent }}>{String(i + 1).padStart(2, "0")}</span>
                  {team}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <ContactSection defaultProgram="The European Circuit" accent={th.accent} />
    </MiniSite>
  );
}
